import React, { useState } from "react";
import { mdiCrosshairsGps } from "@mdi/js";
import MdiIcon from "../../components/device/app/MdiIcon";
import Sheet from "../../components/layout/app/Sheet";
import { AutomationService } from "../../automation/service";
import { useAutomationFeature } from "../../automation/AutomationProvider";
import { errorMessage } from "../../automation/useAutomations";

// "13.08°N, 80.27°E"
export const coordinatesText = (location) =>
  location
    ? `${Math.abs(location.lat).toFixed(2)}°${location.lat >= 0 ? "N" : "S"}, ${Math.abs(location.lon).toFixed(2)}°${
        location.lon >= 0 ? "E" : "W"
      }`
    : "";

// The browser's answer to "where is this device?", as the app's
// app/automation/location.js words the phone's.
const GEO_MESSAGES = {
  1: "Allow location for this site in the browser, or type the coordinates instead.",
  2: "Couldn't find this device's location. Type the coordinates instead.",
  3: "Couldn't find this device's location in time. Try again, or type the coordinates instead.",
};

const currentPosition = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This browser can't share its location. Type the coordinates instead."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
      (error) => reject(new Error(GEO_MESSAGES[error?.code] || GEO_MESSAGES[2])),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  });

// A typed coordinate, or null when it isn't a number in range
const coordinate = (text, limit) => {
  const value = Number(String(text).trim());
  return String(text).trim() !== "" && Number.isFinite(value) && Math.abs(value) <= limit ? value : null;
};

// The home's location, asked for the first time sunrise or sunset is used
// (app/screens/Automation/HomeLocationSheet.js). The web has no address
// search: it takes this device's position, or a latitude and longitude typed
// in. Saved for this home only; nothing tracks anyone.
export default function HomeLocationSheet({ open, homeName, onClose, onSaved }) {
  const { homeId, refresh } = useAutomationFeature();
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const save = async (place) => {
    setBusy("save");
    setError(null);
    try {
      const home = await AutomationService.setHome(homeId, { location: { lat: place.lat, lon: place.lon } });
      await refresh();
      setLat("");
      setLon("");
      onSaved?.(home);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(null);
    }
  };

  const saveCurrent = async () => {
    setBusy("current");
    setError(null);
    try {
      const position = await currentPosition();
      setBusy(null);
      await save(position);
    } catch (locationError) {
      setBusy(null);
      setError(locationError.message);
    }
  };

  const saveTyped = (event) => {
    event.preventDefault();
    const latitude = coordinate(lat, 90);
    const longitude = coordinate(lon, 180);
    if (latitude === null) setError("Enter a latitude from -90 to 90, like 13.0827.");
    else if (longitude === null) setError("Enter a longitude from -180 to 180, like 80.2707.");
    else save({ lat: latitude, lon: longitude });
  };

  return (
    <Sheet open={open} onClose={onClose} title={`Where is ${homeName || "this home"}?`}>
      <div className="hx-auto-sheet">
        <p className="hx-auto-sheet-text">
          Needed once to work out sunrise and sunset times. It's saved for this home only; nothing tracks you.
        </p>
        <button type="button" className="hx-btn hx-auto-sheet-button" onClick={saveCurrent} disabled={Boolean(busy)}>
          <MdiIcon path={mdiCrosshairsGps} size={18} />
          {busy === "current" ? "Finding this device…" : "Use this device's location"}
        </button>
        <p className="hx-auto-step-hint">Use this when you're at home: it saves where this computer or phone is now.</p>
        <p className="hx-auto-or">or type the home's coordinates</p>
        <form className="hx-auto-coords" onSubmit={saveTyped} noValidate>
          <label>
            <span className="hx-auto-step-label">Latitude</span>
            <input
              className="hx-input"
              inputMode="decimal"
              value={lat}
              onChange={(event) => setLat(event.target.value)}
              placeholder="13.0827"
              autoComplete="off"
            />
          </label>
          <label>
            <span className="hx-auto-step-label">Longitude</span>
            <input
              className="hx-input"
              inputMode="decimal"
              value={lon}
              onChange={(event) => setLon(event.target.value)}
              placeholder="80.2707"
              autoComplete="off"
            />
          </label>
          <button type="submit" className="hx-btn" disabled={Boolean(busy)}>
            {busy === "save" ? "Saving…" : "Save"}
          </button>
        </form>
        {error ? (
          <p className="hx-auto-error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="button" className="hx-btn is-ghost hx-auto-not-now" onClick={onClose}>
          Not now
        </button>
      </div>
    </Sheet>
  );
}
